import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Lock } from 'lucide-react'

import { loanAccountQueryOptions } from '@/api/loans-api'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { ApplyForRoundForm } from '@/features/loans/apply-form'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanParams } from '@/features/loans/loan-paths'
import { LOAN_PERMISSIONS, useLoanPermissions } from '@/features/loans/permissions'

/**
 * Applying for the next round.
 *
 * It refuses a narrow display for the same reason the postings do: the principal is a cash amount
 * keyed by hand, and it is the figure the whole round is built on — the schedule, the deposit
 * re-check, the growth cap on the round after this one.
 */
export function ApplyForRoundPage() {
  const params = useLoanParams()
  const accountId = params.accountId ?? ''
  const permissions = useLoanPermissions()

  const query = useQuery(loanAccountQueryOptions(accountId))

  return (
    <RequiresWiderScreen
      screen="Applying for a round"
      reason="The principal is a cash amount keyed by hand, and every figure on the round is built from it."
    >
      <LoanLink
        to={accountId === '' ? loanPaths.accounts : loanPaths.account(accountId)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to the account
      </LoanLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-64 w-full max-w-2xl" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This loan account could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title="Apply for a round"
            reference={query.data.accountNumber}
            description={`${query.data.customerName} · ${query.data.productName}`}
            badges={<StatusBadge status={query.data.status} />}
          />

          {permissions.canApply ? (
            <ApplyForRoundForm account={query.data} />
          ) : (
            <Alert tone="warning">
              <Lock aria-hidden />
              <div className="space-y-1">
                <AlertTitle>Your role cannot capture an application</AlertTitle>
                <AlertDescription>
                  This needs <span className="font-mono text-xs">{LOAN_PERMISSIONS.apply}</span>.
                  The form is not shown rather than shown and refused.
                </AlertDescription>
              </div>
            </Alert>
          )}
        </>
      )}
    </RequiresWiderScreen>
  )
}
